/**
 * Indicador de compartilhamento de tela ativo (Widget)
 * Exibe badge pulsante e timer durante a sessão
 * 
 * @author Sistema
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';

interface ScreenShareIndicatorProps {
  /** Cor primária do widget */
  primaryColor: string;
  /** Callback para encerrar compartilhamento */
  onStop: () => void;
  /** Se true, mostra apenas o badge compacto no header */
  compact?: boolean;
}

/**
 * Indicador visual de compartilhamento de tela ativo
 * 
 * Versão completa: Banner fixo no topo com timer e botão de parar
 * Versão compacta: Badge pequeno com indicador pulsante
 */
export const ScreenShareIndicator: React.FC<ScreenShareIndicatorProps> = ({
  primaryColor,
  onStop,
  compact = false,
}) => {
  // Timer de duração
  const [duration, setDuration] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Inicia timer ao montar
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Formata duração em MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Versão compacta (badge para header)
  if (compact) {
    const badgeStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: 600,
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    };

    const dotStyles: React.CSSProperties = {
      width: '6px',
      height: '6px',
      backgroundColor: '#ffffff',
      borderRadius: '50%',
      animation: 'blink 1s ease-in-out infinite',
    };

    return (
      <>
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.8; }
            }
            @keyframes blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
          `}
        </style>
        <div style={badgeStyles} title="Compartilhando tela">
          <span style={dotStyles} />
          <span>REC</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 400 }}>
            {formatDuration(duration)}
          </span>
        </div>
      </>
    );
  }

  // Versão completa (banner)
  const bannerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    borderBottom: '1px solid #fecaca',
  };

  const leftSectionStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const iconContainerStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const pulseRingStyles: React.CSSProperties = {
    position: 'absolute',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#dc2626',
    opacity: 0.3,
    animation: 'pingPulse 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
  };

  const iconStyles: React.CSSProperties = {
    position: 'relative',
    zIndex: 1,
  };

  const textContainerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#991b1b',
    margin: 0,
  };

  const timerStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#b91c1c',
    fontFamily: 'monospace',
    margin: 0,
  };

  const stopButtonStyles: React.CSSProperties = {
    padding: '6px 14px',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  };

  return (
    <>
      <style>
        {`
          @keyframes pingPulse {
            0% { transform: scale(1); opacity: 0.3; }
            75%, 100% { transform: scale(1.5); opacity: 0; }
          }
        `}
      </style>
      <div style={bannerStyles}>
        <div style={leftSectionStyles}>
          {/* Ícone com pulso */}
          <div style={iconContainerStyles}>
            <span style={pulseRingStyles} />
            <svg 
              style={iconStyles}
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#dc2626" 
              strokeWidth="2"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>

          {/* Texto */}
          <div style={textContainerStyles}>
            <p style={titleStyles}>Compartilhando sua tela</p>
            <p style={timerStyles}>{formatDuration(duration)}</p>
          </div>
        </div>

        {/* Botão de parar */}
        <button
          style={stopButtonStyles}
          onClick={onStop}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#b91c1c';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
          }}
        >
          Parar
        </button>
      </div>
    </>
  );
};
